import { type FC, type ReactNode, useCallback, useMemo } from 'react';
import { RadioButton, Tag } from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { FloatingTooltip } from 'components/floating-tooltip';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { formatNumber } from 'utils/number';
import type {
  PreDrillTargetZoneIntervalRow,
  IntersectedGridsResponse,
  IntersectedGrid,
} from 'types/api-responses';
import {
  permittedDepthIndicatorApplies,
  permittedDepthTooltip,
  intersectedGridReasonText,
  formatGridTvdss,
} from '../utils';
import styles from './change-match-drawer.module.scss';

// DataTable requires the row to be an index signature; the catalog row is a
// fixed shape, so widen it here.
type IntervalRow = PreDrillTargetZoneIntervalRow & Record<string, unknown>;

// With showAll the same interval repeats once per collection (and once per
// loose grid), so row identity is the (interval, collection, grid) triple.
export const intervalRowKey = (row: PreDrillTargetZoneIntervalRow): string =>
  `${row.id}:${row.structureGridCollectionId ?? 'none'}:${
    row.structureGridId ?? 'none'
  }`;

interface IntervalTableProps {
  rows: PreDrillTargetZoneIntervalRow[];
  selectedKey: string | null;
  /** True once an AI run has scored candidates — drives the Confidence cell:
   *  false + null score → "--" (not yet run); true + null → "N/A" (not scored). */
  aiRunHappened: boolean;
  isLoading: boolean;
  /** Story 8: each grid's TVDSS range at the well and where the permit floor sits.
   *  Null when the backend does not serve it; the column then stays out. */
  intersectedGrids?: IntersectedGridsResponse | null;
  onSelect: (row: PreDrillTargetZoneIntervalRow | null) => void;
}

function yesNo(value: boolean): ReactNode {
  return value ? 'Yes' : <span className={styles.boolMuted}>No</span>;
}

// Confidence is a 0–1 score from the AI run; render as a rounded percentage, or
// a muted placeholder ("--" before any run, "N/A" when the interval wasn't scored).
function renderConfidence(
  confidence: number | null,
  aiRunHappened: boolean
): ReactNode {
  if (confidence === null) {
    return (
      <span className={styles.boolMuted}>{aiRunHappened ? 'N/A' : '--'}</span>
    );
  }
  return `${Math.round(confidence * 100)}%`;
}

/**
 * One end of a grid's band under this bore. A grid with no range renders blank with
 * the backend's reason rather than being hidden, so the absence is visible instead of
 * inferred.
 * @param {IntersectedGrid | undefined} grid The grid under this interval, when there is one.
 * @param {'minTvdssFt' | 'maxTvdssFt'} end Which end of the band to print.
 * @return {ReactNode} The cell.
 */
function renderBand(
  grid: IntersectedGrid | undefined,
  end: 'minTvdssFt' | 'maxTvdssFt'
): ReactNode {
  if (!grid) return <span className={styles.boolMuted}>--</span>;
  if (grid[end] === null) {
    const reason = intersectedGridReasonText(grid.reason);
    return (
      <FloatingTooltip content={reason ?? ''} placement="top">
        <span className={styles.boolMuted}>--</span>
      </FloatingTooltip>
    );
  }
  return formatGridTvdss(grid[end]);
}

// Single-select catalog table on the shared DataTable (search, column
// visibility, reset, sorting, pagination). Row click reports via onSelect.
export const IntervalTable: FC<IntervalTableProps> = ({
  rows,
  selectedKey,
  aiRunHappened,
  isLoading,
  intersectedGrids = null,
  onSelect,
}) => {
  const gridsById = useMemo(() => {
    const map = new Map<number, IntersectedGrid>();
    (intersectedGrids?.grids ?? []).forEach((grid) => {
      map.set(grid.structureGridId, grid);
    });
    return map;
  }, [intersectedGrids]);
  // The indicator is withheld when the permit could not be placed at all; the bands
  // are still worth reading, so they render either way.
  const indicatorApplies = permittedDepthIndicatorApplies(intersectedGrids);
  const pillTooltip = permittedDepthTooltip(intersectedGrids);

  const gridFor = useCallback(
    (row: IntervalRow) => {
      if (row.structureGridId === null) return undefined;
      return gridsById.get(row.structureGridId);
    },
    [gridsById]
  );

  const columns = useMemo<DataTableColumnDef<IntervalRow>[]>(
    () => [
      {
        // Leading radio column — a pure visual mirror of row.getIsSelected().
        // readOnly, doesn't stop propagation: the click bubbles to the row.
        id: '_radio',
        header: '',
        size: 56,
        minSize: 56,
        enableSorting: false,
        enableHiding: false,
        cell: (info) => (
          <span role="presentation" className={styles.radioCell}>
            <RadioButton
              id={`change-match-interval-${intervalRowKey(info.row.original)}`}
              name="change-match-interval"
              value={intervalRowKey(info.row.original)}
              checked={info.row.getIsSelected()}
              labelText=""
              readOnly
            />
          </span>
        ),
      },
      {
        id: 'geologicInterval',
        accessorKey: 'geologicInterval',
        header: 'Geologic Interval',
        size: 220,
        cell: (info) => (
          <span className={styles.nameCell}>{String(info.getValue())}</span>
        ),
      },
      // Where each interval's grid sits at this well. Absent on a backend that does
      // not serve the intersected grids, and the column then stays out entirely.
      ...(intersectedGrids
        ? [
            {
              id: 'minTvdss',
              header: 'Min TVDSS (ft)',
              size: 150,
              enableSorting: false,
              accessorFn: (row: IntervalRow) =>
                gridFor(row)?.minTvdssFt ?? null,
              cell: (info: { row: { original: IntervalRow } }) =>
                renderBand(gridFor(info.row.original), 'minTvdssFt'),
            } as DataTableColumnDef<IntervalRow>,
            {
              id: 'maxTvdss',
              header: 'Max TVDSS (ft)',
              size: 150,
              enableSorting: false,
              accessorFn: (row: IntervalRow) =>
                gridFor(row)?.maxTvdssFt ?? null,
              cell: (info: { row: { original: IntervalRow } }) =>
                renderBand(gridFor(info.row.original), 'maxTvdssFt'),
            } as DataTableColumnDef<IntervalRow>,
            {
              // The separate 3D fact: whether the current geometry actually enters
              // the interval. It never suppresses a band or the indicator.
              id: 'boreEnters',
              header: 'Bore Enters',
              size: 130,
              enableSorting: false,
              accessorFn: (row: IntervalRow) =>
                gridFor(row)?.boreEntersInterval ?? null,
              cell: (info: { row: { original: IntervalRow } }) => {
                const enters = gridFor(info.row.original)?.boreEntersInterval;
                if (enters === null || enters === undefined) {
                  return <span className={styles.boolMuted}>--</span>;
                }
                return yesNo(enters);
              },
            } as DataTableColumnDef<IntervalRow>,
          ]
        : []),
      {
        id: 'confidence',
        accessorKey: 'confidence',
        header: () => (
          <span className={styles.confidenceHeader}>
            Confidence
            <FloatingTooltip
              content="Confidence is derived from the AI agent's target-zone analysis. Scores appear only after the agent has run, and only for intervals in the selected Structure Grid Collection."
              placement="top"
            >
              <Information className={styles.headerInfoIcon} />
            </FloatingTooltip>
          </span>
        ),
        size: 170,
        cell: (info) => {
          // Every grid the backend flags carries the pill. Where a horizon crosses
          // the permitted depth two intervals legitimately contain it, and electing
          // one would be a guess.
          const containsPermittedDepth =
            indicatorApplies &&
            gridFor(info.row.original)?.containsPermittedDepth === true;
          return (
            <span className={styles.confidenceCell}>
              {renderConfidence(info.row.original.confidence, aiRunHappened)}
              {/* The pill rides alongside the score: both answer "is this the
                  zone?", and the depth is the strongest hint available before the
                  agent has run. */}
              {containsPermittedDepth && (
                <FloatingTooltip content={pillTooltip} placement="top">
                  <Tag type="blue" size="sm">
                    Permitted TVD
                  </Tag>
                </FloatingTooltip>
              )}
            </span>
          );
        },
      },
      {
        id: 'basinName',
        accessorKey: 'basinName',
        header: 'Basin',
        size: 130,
      },
      {
        id: 'structureGridCollection',
        accessorKey: 'structureGridCollection',
        header: 'Structure Grid Collection',
        size: 220,
        cell: (info) => (
          <span className={styles.collectionCell}>
            {info.row.original.structureGridCollection ?? '—'}
            {info.row.original.isDefaultCollection && (
              <Tag type="gray" size="sm">
                Default
              </Tag>
            )}
          </span>
        ),
      },
      {
        id: 'hasGrid',
        accessorKey: 'hasGrid',
        header: 'Has Grid',
        size: 110,
        cell: (info) => yesNo(info.row.original.hasGrid),
      },
      {
        id: 'structureGridName',
        accessorKey: 'structureGridName',
        header: 'Grid',
        size: 180,
        cell: (info) =>
          info.row.original.structureGridName ??
          info.row.original.structureGridId ?? (
            <span className={styles.boolMuted}>—</span>
          ),
      },
      {
        id: 'locationIntersectGrid',
        accessorKey: 'locationIntersectGrid',
        header: 'Location Intersect Grid',
        size: 180,
        cell: (info) => yesNo(info.row.original.locationIntersectGrid),
      },
      {
        id: 'hasWellWithLandingZone',
        accessorKey: 'hasWellWithLandingZone',
        header: 'Has Well with Landing Zone',
        size: 200,
        cell: (info) => yesNo(info.row.original.hasWellWithLandingZone),
      },
      {
        id: 'positionalIndex',
        accessorKey: 'positionalIndex',
        header: 'Positional Index',
        size: 150,
        cell: (info) => formatNumber(info.row.original.positionalIndex, 0, 2),
      },
    ],
    [aiRunHappened, intersectedGrids, gridFor, indicatorApplies, pillTooltip]
  );

  return (
    <DataTable<IntervalRow>
      data={rows as IntervalRow[]}
      columns={columns}
      getRowId={(row) => intervalRowKey(row)}
      isLoading={isLoading}
      scroll={{ mode: 'paginated' }}
      pageSizes={[10, 25, 50]}
      initialTableState={{
        pageSize: 10,
        selection: {
          selectAll: false,
          toggledNodes: selectedKey === null ? [] : [selectedKey],
        },
      }}
      onSelectionChange={(s) => {
        const key = s.selection.toggledNodes[0];
        onSelect(
          key === undefined
            ? null
            : (rows.find((r) => intervalRowKey(r) === key) ?? null)
        );
      }}
      config={{
        selectionType: 'single',
        globalSearch: true,
        columnVisibility: true,
        sorting: true,
        columnFilters: false,
        export: false,
      }}
    />
  );
};
